import { v4 as uuidv4 } from 'uuid'

import type { Polygon } from '@/modules/Editor/AnnotationData'
import { createAndSelectAnnotation } from '@/modules/Editor/actions/utils/createAndSelectAnnotation'
import { EditorEvents, ToolEvents } from '@/modules/Editor/eventBus'
import type { Action } from '@/modules/Editor/managers/actionManager'
import type { Annotation } from '@/modules/Editor/models/annotation/Annotation'
import { shallowCloneAnnotation } from '@/modules/Editor/models/annotation/cloneAnnotation'
import type { Raster } from '@/modules/Editor/models/raster/Raster'
import { assertImageRaster } from '@/modules/Editor/models/raster/assertImageRaster'
import { assertVideoRaster } from '@/modules/Editor/models/raster/assertVideoRaster'
import { RasterTypes } from '@/modules/Editor/models/raster/rasterTypes'
import { createUpdateRasterAction } from '@/modules/Editor/plugins/mask/utils/shared/createUpdateRasterAction'
import { getOrCreateRasterForView } from '@/modules/Editor/plugins/mask/utils/shared/getOrCreateRasterForView'
import { getRasterBufferAccessorForView } from '@/modules/Editor/plugins/mask/utils/shared/getRasterBufferAccessorForView'
import { updateMaskAnnotation } from '@/modules/Editor/plugins/mask/utils/shared/updateMaskAnnotation'
import { createEditablePoint } from '@/modules/Editor/point'
import { ToolName } from '@/modules/Editor/tools/types'
import type { View } from '@/modules/Editor/views/view'

import type { PolygonShape } from './geometry'
import { MESSAGES } from './messages'
import type { MaskCrop } from './maskCrop'
import { cropLabel, forEachSetPixel, maskBounds, sameCrop } from './maskCrop'
import type { MaskSlicePlan } from './planMaskSlice'
import type { PolygonSlicePlan } from './planPolygonSlice'
import { withSliceData } from './sliceTarget'

const toPolygonData = (
  shape: PolygonShape,
): Required<Pick<Polygon, 'path' | 'additionalPaths'>> => ({
  path: shape.path.map(createEditablePoint),
  additionalPaths: shape.additionalPaths.map((ring) => ring.map(createEditablePoint)),
})

const fail = (): false => {
  EditorEvents.message.emit({ content: MESSAGES.failed, level: 'error' })
  return false
}

export const slicePolygonAction = (
  view: View,
  target: Annotation,
  keep: PolygonShape,
  created: Annotation,
): Action => {
  const previousData = target.data
  const restoreTarget = (): void => {
    view.annotationManager.updateAnnotation(shallowCloneAnnotation(target, { data: previousData }))
  }
  return {
    do(): boolean {
      try {
        view.annotationManager.updateAnnotation(
          shallowCloneAnnotation(target, {
            data: withSliceData(view, target, toPolygonData(keep)),
          }),
        )
      } catch {
        return fail()
      }
      // locks the target until its save lands, as merge and subtract do; emitted only once an
      // update is queued, since the lock is released by that save
      ToolEvents.polygonUpdating.emit(
        { toolName: ToolName.Edit, lastHotkeyPressed: null, editing: false },
        [target.id],
      )
      try {
        view.annotationManager.createAnnotation(created)
      } catch {
        restoreTarget()
        return fail()
      }
      view.annotationManager.selectAnnotations([created.id])
      return true
    },
    undo(): boolean {
      view.annotationManager.deleteAnnotation(created.id)
      restoreTarget()
      view.annotationManager.selectAnnotations([target.id])
      return true
    },
  }
}

export const applyPolygonSlice = async (
  view: View,
  target: Annotation,
  plan: PolygonSlicePlan,
  isCurrent: () => boolean,
): Promise<boolean> => {
  const created = await view.annotationManager.prepareAnnotationForCreation({
    type: 'polygon',
    data: toPolygonData(plan.piece),
    annotationClass: target.annotationClass,
  })
  // preparation may await an instance ID; the slice can be cancelled or the target removed meanwhile
  if (!isCurrent() || !view.annotationManager.getAnnotation(target.id)) {
    return false
  }
  if (!created) {
    return fail()
  }
  const action = slicePolygonAction(view, target, plan.keep, created)
  if (!action.do()) {
    return false
  }
  view.actionManager.done(action)
  return true
}

export const readMaskCrop = (view: View, target: Annotation): MaskCrop | null => {
  const raster = getOrCreateRasterForView(view)
  const label = raster.getLabelIndexForAnnotationId(target.id)
  const bounds = raster.getAnnotationVisualBounds(target.id)
  if (label === undefined || !bounds) {
    return null
  }
  return cropLabel(getRasterBufferAccessorForView(view, raster), label, bounds)
}

// DICOM masks live in a per-frame raster; the new mask starts as a keyframe on the current slice.
const isFramedRaster = (raster: Raster): boolean =>
  raster.type === RasterTypes.VIDEO || raster.type === RasterTypes.VOXEL

const nextLabel = (raster: ReturnType<typeof getOrCreateRasterForView>): number | null => {
  try {
    return raster.getNextAvailableLabelIndex()
  } catch {
    EditorEvents.message.emit({ content: MESSAGES.maskLimit, level: 'warning' })
    return null
  }
}

export const applyMaskSlice = async (
  view: View,
  target: Annotation,
  crop: MaskCrop,
  plan: MaskSlicePlan,
  isCurrent: () => boolean,
): Promise<boolean> => {
  if (!isCurrent()) {
    return false
  }
  const raster = getOrCreateRasterForView(view)
  if (nextLabel(raster) === null) {
    return false
  }

  const frame = view.currentFrameIndex
  const framed = isFramedRaster(raster)
  // the only await happens before the raster is touched
  const created = await view.annotationManager.prepareAnnotationForCreation({
    type: 'mask',
    id: uuidv4(),
    data: framed
      ? {
          frames: { [frame]: { rasterId: raster.id } },
          sub_frames: {},
          segments: [[frame, frame + 1]],
          interpolated: false,
          interpolate_algorithm: 'linear-1.1',
        }
      : { rasterId: raster.id },
    annotationClass: target.annotationClass,
  })
  if (!isCurrent() || !view.annotationManager.getAnnotation(target.id)) {
    return false
  }
  const label = raster.getLabelIndexForAnnotationId(target.id)
  const current = readMaskCrop(view, target)
  if (label === undefined || !current || !created) {
    return fail()
  }
  if (!sameCrop(current, crop)) {
    EditorEvents.message.emit({ content: MESSAGES.targetChanged, level: 'warning' })
    return false
  }
  const newLabel = nextLabel(raster)
  if (newLabel === null) {
    return false
  }

  const frames = [frame]
  const labels = [label, newLabel]
  const changed = maskBounds(crop, crop.data)
  const before = raster.takeSnapshot(frames)
  try {
    const accessor = getRasterBufferAccessorForView(view, raster)
    forEachSetPixel(crop, plan.piece, (x, y) => accessor.set(x, y, newLabel))
    raster.setAnnotationMapping(newLabel, created.id, target.classId)
    const pieceBounds = maskBounds(crop, plan.piece)
    if (framed) {
      assertVideoRaster(raster).setLabelOnKeyframe(newLabel, frame, pieceBounds)
    } else {
      assertImageRaster(raster).setBoundsForLabelIndex(newLabel, pieceBounds)
    }
    createAndSelectAnnotation(view, created)
    if (framed && 'segments' in created.data && created.data.segments) {
      // prepareAnnotationForCreation opens the segment at [frame, frame]; masks span to frame + 1
      created.data.segments[0] = [frame, frame + 1]
      view.annotationManager.updateAnnotation(created, { updatedFramesIndices: frames })
    }
    updateMaskAnnotation(view, raster, target, maskBounds(crop, plan.keep), frames)
  } catch {
    // restores pixels, mappings and bounds; the snapshot restore keeps extra labels, so drop ours
    createUpdateRasterAction(view, raster.takeSnapshot(frames), before, labels).undo()
    if (raster.getAnnotationMapping(newLabel) !== undefined) {
      raster.deleteAnnotationMapping(newLabel)
    }
    if (view.annotationManager.getAnnotation(created.id)) {
      view.annotationManager.deleteAnnotation(created.id)
    }
    view.annotationManager.selectAnnotations([target.id])
    return fail()
  } finally {
    raster.invalidate(
      changed.topLeft.x,
      changed.bottomRight.x,
      changed.topLeft.y,
      changed.bottomRight.y,
    )
  }
  view.actionManager.done(
    createUpdateRasterAction(view, raster.takeSnapshot(frames), before, labels),
  )
  return true
}
